import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import {
  SearchInputV2,
  BulkActionsBar,
  BaseModalV2,
  TableSkeleton,
  PaginationSkeleton,
} from '../../components/common';
import PaginatorV2 from '../../components/common/PaginatorV2';
import { PrimaryButton, SecondaryButton } from '../../components/common/ButtonsV2';
import { BeaconLocationIcon } from '../../components/common/Icons';
import Table from '../../components/common/Table/Table';
import { LoadingContext } from '../../context';
import {
  useExportData,
  useBeaconsData,
  useGetAllOrganizationsAndFacilities,
  useBeaconLocationActions,
  usePagination,
} from '../../hooks';
import {
  prepareRowForEditing,
  generateTableColumns,
  mapBeaconsToTableRows,
  getColumnsHeaderForExportData,
  createBeaconParamsToEdit,
  formatBeaconDataForTableToExport,
} from '../../utils/normalizeBeaconsAssociations';
import BeaconModal from './BeaconModal';
import MainPageLayout from '../../components/common/MainPageLayout';
import HeaderControls from '../../components/common/HeaderControls';

const BeaconLocation = () => {
  const [showBeaconModal, setShowBeaconModal] = useState(false); // Modal for beacon add/edit
  const [editBeaconModalData, setEditBeaconModalData] = useState({}); // Data for beacon edit
  const [selectedRows, setSelectedRows] = useState([]); // Array of selected rows for bulk actions
  const [searchValue, setSearchValue] = useState(''); // Search input value
  const [selectAll, setSelectAll] = useState(false); // Checkbox to select all rows
  const [associationsToDelete, setAssociationsToDelete] = useState(false); // Beacons selected for deletion
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false); // Modal for confirmation of bulk delete
  const [currentRecords, setCurrentRecords] = useState([]); // Current beacon records for table
  const { setIsLoading } = useContext(LoadingContext);
  const { beacons, fetchBeacons, beaconsIsloading } = useBeaconsData();
  const { handleEditBeacon, handleAddBeacon, handleDelete } = useBeaconLocationActions(
    setIsLoading,
    fetchBeacons,
    setSelectedRows,
  );
  const userData = useSelector((state) => state?.auth?.user);

  const {
    currentPage,
    currentRecords: tableCurrentRecords,
    totalPages,
    getPaginatedPageNumbers,
    handlePageChange,
    goToPrevPage,
    goToNextPage,
  } = usePagination(beacons, 10);

  const userOrganization = userData?.organization ?? userData?.organizationId;
  const isUserAdmin = userData?.role === 'admin';
  const { organizations, facilities } = useGetAllOrganizationsAndFacilities(
    isUserAdmin,
    userOrganization,
  );
  const recordsForExportdata = formatBeaconDataForTableToExport(beacons, organizations, facilities);
  const handleExport = useExportData(
    recordsForExportdata,
    getColumnsHeaderForExportData,
    'Beacon & Location Management',
  );

  useEffect(() => {
    setCurrentRecords(beacons);
  }, [beacons]);

  useEffect(() => {
    fetchBeacons();
  }, [fetchBeacons]);

  useEffect(() => {
    if (!_.isEmpty(editBeaconModalData)) setShowBeaconModal(true);
  }, [editBeaconModalData]);

  useEffect(() => {
    if (tableCurrentRecords.length !== selectedRows.length) {
      setSelectAll(false);
    }
  }, [selectedRows.length, tableCurrentRecords.length]);

  const handleSubmit = ({ beaconIdentifier, locationName, facility, id, organization_id }) => {
    const params = createBeaconParamsToEdit({
      beaconIdentifier,
      locationName,
      facility,
      id,
      organization_id,
    });

    if (!_.isEmpty(editBeaconModalData)) {
      handleEditBeacon(id, params);
    } else {
      handleAddBeacon(params);
    }

    setShowBeaconModal(false);
  };

  const selectedRowToDelete = (beaconMacAddressSelected) => {
    const { id } = tableCurrentRecords.find(({ id }) => beaconMacAddressSelected === id);
    setAssociationsToDelete([id]);
    setShowConfirmDeleteModal(true);
  };

  const onConfirmationDeleteModal = () => {
    handleDelete(associationsToDelete);
    setShowConfirmDeleteModal(false);
  };

  const selectedRowToEdit = (beaconMacAddressSelected) => {
    const associationToEdit = tableCurrentRecords.find(({ id }) => beaconMacAddressSelected === id);
    const associationNormalized = prepareRowForEditing(associationToEdit);
    setEditBeaconModalData(associationNormalized);
  };

  const handleMultipleDelete = () => {
    setAssociationsToDelete(selectedRows);
    setShowConfirmDeleteModal(true);
  };

  const onSearch = (tableItems, value) => {
    if (!value) return tableItems;

    const lowerValue = value.toLowerCase();
    const keysToSearch = ['location', 'location_semantic', 'beacon_mac_address'];

    return beacons.filter((beacon) =>
      keysToSearch.some((key) => beacon[key]?.toLowerCase().includes(lowerValue)),
    );
  };

  const onClickAddBeacon = () => {
    setEditBeaconModalData({});
    setShowBeaconModal(true);
  };

  const onClickSelectAll = (selectAll) => {
    if (selectAll) {
      setSelectedRows((prevSelected) => {
        const newIds = tableCurrentRecords.map((item) => item.id);
        return Array.from(new Set([...prevSelected, ...newIds]));
      });
    } else {
      setSelectedRows([]);
    }
    setSelectAll(selectAll);
  };

  const onChangeCheckbox = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id],
    );
  };

  const filteredBeacons = searchValue ? onSearch(currentRecords, searchValue) : tableCurrentRecords;
  const modalTitle = !_.isEmpty(editBeaconModalData) ? 'Edit Beacon' : 'Add Beacon';
  const columns = generateTableColumns(selectAll, onClickSelectAll, isUserAdmin);
  const rows = mapBeaconsToTableRows(
    filteredBeacons,
    organizations,
    facilities,
    selectedRows,
    onChangeCheckbox,
    isUserAdmin,
    selectedRowToDelete,
    selectedRowToEdit,
  );

  return (
    <MainPageLayout
      pageTitle="Beacon & Location Management"
      icon={<BeaconLocationIcon />}
      headerControls={<HeaderControls onExport={handleExport} onRefresh={fetchBeacons} />}
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-2 pt-[10px] pb-4 mb-2">
        <SearchInputV2 value={searchValue} onChange={setSearchValue} />
        <PrimaryButton onClick={onClickAddBeacon} customClass="self-end mt-5 md:mt-0 md:self-auto">
          <span className="text-2xl leading-[18px]">+</span> Add Beacon
        </PrimaryButton>
      </div>

      <div className="flex-grow">
        {beaconsIsloading ? (
          <TableSkeleton columns={columns} />
        ) : (
          <Table columns={columns} data={rows} />
        )}
        {beaconsIsloading ? (
          <PaginationSkeleton />
        ) : (
          <PaginatorV2
            getPaginatedPageNumbers={getPaginatedPageNumbers}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            total={totalPages}
            goToPrevPage={goToPrevPage}
            goToNextPage={goToNextPage}
          />
        )}
        {showBeaconModal && (
          <BeaconModal
            title={modalTitle}
            isUserAdmin={isUserAdmin}
            organizations={organizations}
            onSubmit={handleSubmit}
            onClose={() => setShowBeaconModal(false)}
            userOrganization={userOrganization}
            initialData={editBeaconModalData}
          />
        )}
        <BulkActionsBar
          selectedCount={selectedRows.length}
          onDelete={handleMultipleDelete}
          show={selectedRows.length}
        />

        {showConfirmDeleteModal && (
          <BaseModalV2 title="Delete Selection" onClose={() => setShowConfirmDeleteModal(false)}>
            <p className="text-black text-center text-[16px] sm:text-[18px] mt-[45px]">
              You are about to delete{' '}
              <span className="text-custom-light-blue font-bold">
                {associationsToDelete.length}
              </span>{' '}
              associations. This action cannot be undone.
            </p>
            <p className="text-center text-[16px] sm:text-[20px] mt-[29px]">
              Do you want to proceed?
            </p>
            <div className="flex justify-between mt-[45px] gap-[32px]">
              <SecondaryButton fullWidth onClick={() => setShowConfirmDeleteModal(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton fullWidth onClick={onConfirmationDeleteModal}>
                Confirm
              </PrimaryButton>
            </div>
          </BaseModalV2>
        )}
      </div>
    </MainPageLayout>
  );
};

export default BeaconLocation;
